'use client'

import React from 'react';
import { builder, BuilderContent } from "@builder.io/sdk";
import { useEffect, useState } from "react";
import { Skeleton } from '@/components/ui/skeleton';
import { SpacingType, sectionVariants } from '@/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { PageContext } from '../builder';
import { useDebounce } from '@/hooks/useDebounce';
import { SectionWrapper } from '../components/SectionWraper';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { EmptyResult } from '../components/EmptyResult';
import parse from 'html-react-parser';
import { cn, variantBackgroundColors } from '@/lib/utils';

interface UseCasesListingProps {  
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  numbersPerRow?: "2" | "3" | "4";
  attributes?: React.HTMLProps<HTMLElement>;
}

interface BuilderQuery {
  'data.title'?: {
    $ne: string;
  };
  $or?: Array<{
    name?: { $regex: string; $options: string };
  }>;
}

  export function UseCasesListing({ spacing = 'xlarge', darkMode = false, variant = 'A', usedInPageNav = false, pageNavTitle = 'Use Cases', themeChangeOnScroll = false, title, numbersPerRow, attributes }: UseCasesListingProps) {
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchedItems, setFetchedItems] = useState<BuilderContent[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [query, setQuery] = useState<BuilderQuery>({});
  const debouncedQuery = useDebounce(query, 500);

  const pageFields = React.useContext(PageContext);
  const { articlesData, itemsPerPage } = (pageFields.data as unknown as { articlesData?: BuilderContent[]; itemsPerPage?: number }) ?? {};

  // Update query when search changes
  useEffect(() => {
    const newQuery: BuilderQuery = {
      'data.title': {
        $ne: 'Business Use Cases for Working Capital Solutions'
      }
    };
    
    if (debouncedSearchTerm) {
      newQuery.$or = [
        { name: { $regex: debouncedSearchTerm, $options: 'i' } }
      ];
    }

    setQuery(newQuery);
  }, [debouncedSearchTerm]);

  // Use debouncedQuery for data fetching
  const fetchFilteredData = React.useCallback(async () => {
    setLoading(true);
    try {
      const articles = await builder.getAll('use-cases', {
        limit: (itemsPerPage ?? 0) + 1,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });
      
      setFetchedItems(articles.slice(0, itemsPerPage ?? 0));
      setHasMore(articles.length > (itemsPerPage ?? 0));
      setLoading(false);
    } catch (err) {
      console.error("Filter fetch failed", err);
      setLoading(false);
    }
  }, [debouncedQuery, itemsPerPage]);
  
  const loadMore = React.useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const newOffset = offset + (itemsPerPage ?? 0);
      
      const newArticles = await builder.getAll('use-cases', {
        limit: (itemsPerPage ?? 0) + 1,
        offset: newOffset,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      if (newArticles.length > 0) {
        setFetchedItems(prev => [...prev, ...newArticles.slice(0, itemsPerPage ?? 0)]);
        setOffset(newOffset);
        setHasMore(newArticles.length > (itemsPerPage ?? 0));
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Load more failed", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [offset, itemsPerPage, debouncedQuery]);

  // Initial data load and filter updates
  useEffect(() => {
    // If we have articlesData from context, use it for initial load
    if (articlesData && !debouncedQuery.$or) {
      setFetchedItems(articlesData.slice(0, itemsPerPage ?? 0) || []);
      setHasMore(articlesData.length > (itemsPerPage ?? 0));
      setLoading(false);
    } else {
      // Otherwise fetch with current filters
      fetchFilteredData();
    }
  }, [articlesData, itemsPerPage, debouncedQuery, fetchFilteredData]);

  useEffect(() => {
    if (fetchedItems?.length > 0) {
      setLoading(false);
    }
  }, [fetchedItems]);

  const inputBackgroundColor = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="case-studies-list"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        <div className="flex flex-col md:flex-row gap-12 justify-between mb-6 md:mb-32">
          <h2>
            {title}
          </h2>
          <div className="relative">
            <Input 
              type="text" 
              placeholder="Search"  
              className={cn(inputBackgroundColor, "w-full md:w-80 pl-9")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="material-symbols-outlined w-4.5 h-4.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ fontSize: '18px' }}>search</span>
          </div>
        </div>
        <div className="">
          {loading ? (
            <div 
              className={cn(
                "grid gap-5",
                numbersPerRow === "2" ? "md:grid-cols-2" :
                numbersPerRow === "3" ? "md:grid-cols-3" :
                numbersPerRow === "4" ? "md:grid-cols-4" :
                "md:grid-cols-2"
              )}>
              {fetchedItems && fetchedItems.length > 0 ? (
                fetchedItems.map((item: BuilderContent) => {
                const url = item.data?.url || "";
                return (
                  <React.Fragment key={item.id}>
                    <Link href={url} tabIndex={-1} className="grid grid-cols-1">
                      <Card
                        key={item.id}
                        className="flex p-6 shadow-md border-none gap-4"
                      >
                        <CardHeader className="py-4 px-0 gap-0">
                          <Skeleton className="h-7 w-[100%]" />   
                        </CardHeader>
                        <CardContent className="p-0 flex-1">
                          <Skeleton className="h-4 w-[100%] mb-2" />
                          <Skeleton className="h-4 w-[100%] mb-2" />
                          <Skeleton className="h-4 w-[100%] mb-2" />
                        </CardContent>
                        <CardFooter className="p-0 mt-10">
                          <Skeleton className="h-11 w-11 rounded-full" />
                        </CardFooter>
                      </Card>
                    </Link>
                  </React.Fragment>
                  );
                })
              ) : (
                <div className="col-span-2">
                  <EmptyResult
                    className="mt-16"
                    buttonOnClick={() => {
                      setLoading(true);
                      setSearchTerm("");
                      // Force query update with empty search
                      setQuery({});
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              <div 
                className={cn(
                  "grid gap-5",
                  numbersPerRow === "2" ? "md:grid-cols-2" :
                  numbersPerRow === "3" ? "md:grid-cols-3" :
                  numbersPerRow === "4" ? "md:grid-cols-4" :
                  "md:grid-cols-2"
                )}>
                {fetchedItems && fetchedItems.length > 0 ? (
                  fetchedItems.map((item: BuilderContent) => {
                  const title = item.data?.title || "";
                  const url = item.data?.url || "";
                  const description = item.data?.description || "";
                  return (
                    <React.Fragment key={item.id}>
                       <Link href={url} tabIndex={-1} className="grid grid-cols-1">
                        <Card
                          key={item.id}
                          className="flex p-6 shadow-md border-none gap-4"
                        >
                          <CardHeader className="py-4 px-0 gap-0">
                            <h6>
                              {parse(title)}
                            </h6>
                          </CardHeader>
                          <CardContent className="p-0 flex-1">
                            <p className="paragraph-base text-muted-foreground">{description}</p>
                          </CardContent>
                          <CardFooter className="p-0 mt-10">
                            <Button variant="outline" size="iconLg" grayBorder lifted>
                              <span className="material-symbols-outlined text-foreground group-hover/gallery2:text-primary-foreground">
                                arrow_forward
                              </span>                      
                            </Button>
                          </CardFooter>
                        </Card>
                      </Link>
                    </React.Fragment>
                    );
                  })
                ) : (
                  <div className="col-span-2">
                    <EmptyResult
                      className="mt-16"
                      buttonOnClick={() => {
                        setLoading(true);
                        setSearchTerm("");
                        // Force query update with empty search
                        setQuery({});
                      }}
                    />
                  </div>
                )}
              </div>
              {hasMore && (
              <div className="mt-16 text-center">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full md:w-auto cursor-pointer"
                  onClick={loadMore}
                  disabled={!hasMore || isLoadingMore}
                > 
                  {isLoadingMore ? 'Loading...' : hasMore ? 'Load more use cases' : 'No more articles'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
} 